import React, { useState, useEffect, useCallback, useRef } from 'react';
import JSZip from 'jszip';
import CONNECTOR_WRAPPER_SOURCE from '../constants/connectorWrapperSource';
import { v2ConnectorsService } from '../services/v2ConnectorsService';
import ConnectorCodeAssistant from './ConnectorCodeAssistant';
import ConfirmModal from './shared/ConfirmModal';

// The connector code body belongs to the MARKETPLACE (marketplace_code_files) and is shared across
// every connector of that marketplace. A connector leverages this code and chooses ONE file as its
// entry point (★). Editing a file edits the shared file; choosing an entry point is per-connector.

// Build a nested tree from flat [{id, path, is_directory, content}] rows.
function buildTree(files) {
  const root = { name: '', path: '', isDir: true, children: {} };
  [...files].sort((a, b) => a.path.localeCompare(b.path)).forEach(f => {
    const parts = String(f.path).split('/').filter(Boolean);
    let node = root;
    let acc = '';
    parts.forEach((part, i) => {
      acc = acc ? `${acc}/${part}` : part;
      const isLast = i === parts.length - 1;
      if (!node.children[part]) node.children[part] = { name: part, path: acc, isDir: true, children: {}, id: undefined, content: undefined };
      const child = node.children[part];
      if (isLast) { child.id = f.id; child.isDir = !!f.is_directory; if (!child.isDir) child.content = f.content; }
      node = child;
    });
  });
  return root;
}

const buildHandlerSource = (entryRequire) => `// handler.js — Lambda-style entrypoint for this connector bundle.
// Invoke as handler.handler(event, context):
//   event   = { credentialId }   // entity_credentials.id (also accepts credential_id)
//   context = { mysql, pwkey }   // a MySQL client (async query) + the pwkey secret
const Connector = require('${entryRequire}');

exports.handler = async (event = {}, context = {}) => {
  const credentialId = event.credentialId != null ? event.credentialId : event.credential_id;
  const connector = new Connector({ mysql: context.mysql, pwkey: context.pwkey, credentialId });
  return connector.executeWrapper();
};
`;

const skeletonSource = (cls) => `const ConnectorWrapper = require('./connector-wrapper');

class ${cls} extends ConnectorWrapper {
  async execute(credentials) {
    // credentials.settings holds this connector's decrypted config
    // ...
  }
}

module.exports = ${cls};
`;

const TEST_CONNECTOR_JS = `// Smoke test — run with: npm test
const { handler } = require('../handler');

(async () => {
  const event = { credentialId: process.env.TEST_CREDENTIAL_ID };
  const context = { mysql: null /* your MySQL client */, pwkey: process.env.PWKEY };
  try {
    const result = await handler(event, context);
    console.log('OK', JSON.stringify(result, null, 2));
  } catch (err) {
    console.error('FAILED', err);
    process.exit(1);
  }
})();
`;

const DEFAULT_PACKAGE_JSON = JSON.stringify({
  name: 'connector', version: '1.0.0', private: true, main: 'index.js',
  scripts: { test: 'node __test__/test-connector.js' }, dependencies: {},
}, null, 2) + '\n';

const ConnectorFiles = ({ connectorId, marketplaceId, entrypoint, assistantContext }) => {
  const [files, setFiles] = useState([]);
  const [selectedKey, setSelectedKey] = useState(null); // selected file id
  const [fileContent, setFileContent] = useState('');
  const [fileDirty, setFileDirty] = useState(false);
  const [adding, setAdding] = useState(null); // { parentPath, kind: 'file'|'dir' }
  const [addName, setAddName] = useState('');
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(null); // 'zip' | null
  const [dragOverPath, setDragOverPath] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null); // { fileId, dirPath, label }
  const uploadRef = useRef(null);

  const canEdit = !!connectorId && !!marketplaceId;

  const load = useCallback(async (selectId) => {
    if (!connectorId || !marketplaceId) { setFiles([]); return; }
    try {
      const res = await v2ConnectorsService.files.list(connectorId, marketplaceId);
      const list = res.files || [];
      setFiles(list);
      if (selectId != null) {
        const f = list.find(x => x.id === selectId);
        if (f && !f.is_directory) { setSelectedKey(f.id); setFileContent(f.content || ''); setFileDirty(false); }
      }
    } catch (err) {
      setError(err.message || 'Failed to load files');
    }
  }, [connectorId, marketplaceId]);

  useEffect(() => { load(); }, [load]);
  // Switching marketplace changes the whole code body — reset the current selection.
  useEffect(() => { setSelectedKey(null); setFileContent(''); setFileDirty(false); }, [marketplaceId]);

  const selectedFile = files.find(f => f.id === selectedKey);
  const selectedName = selectedFile ? selectedFile.path : '';

  const selectFileNode = (node) => {
    if (node.isDir) return;
    setSelectedKey(node.id);
    const f = files.find(x => x.id === node.id);
    setFileContent(f ? (f.content || '') : '');
    setFileDirty(false);
    setError(null);
  };

  const onEditorChange = (v) => { setFileContent(v); setFileDirty(true); };
  const onAssistantInsert = (v) => onEditorChange(v);

  const className = ((assistantContext && assistantContext.name) || '')
    .replace(/[^a-zA-Z0-9]+/g, ' ').trim().split(' ').filter(Boolean)
    .map(w => w.charAt(0).toUpperCase() + w.slice(1)).join('') || 'MyConnector';

  const insertSkeleton = () => onEditorChange(skeletonSource(className));

  // Scaffold a new connector: entry point skeleton + test + package.json (only what's missing).
  const initialize = async () => {
    if (!canEdit) return;
    const entryPath = (entrypoint && entrypoint.trim()) || 'index.js';
    const existing = new Set(files.map(f => f.path));
    const toCreate = [];
    if (!existing.has(entryPath)) toCreate.push({ path: entryPath, content: skeletonSource(className) });
    if (!existing.has('__test__/test-connector.js')) toCreate.push({ path: '__test__/test-connector.js', content: TEST_CONNECTOR_JS });
    if (!existing.has('package.json')) toCreate.push({ path: 'package.json', content: DEFAULT_PACKAGE_JSON });
    if (!toCreate.length) { setError('Already initialized.'); return; }
    setBusy('init');
    setError(null);
    try {
      let entryId = null;
      for (const f of toCreate) {
        const r = await v2ConnectorsService.files.create(connectorId, { path: f.path, is_directory: false, content: f.content }, marketplaceId);
        if (f.path === entryPath) entryId = r.id;
      }
      await load(entryId);
    } catch (err) {
      setError(err.message || 'Failed to initialize');
    } finally {
      setBusy(null);
    }
  };

  const canAdd = canEdit;
  const startAdd = (parentPath, kind) => { if (!canAdd) return; setAdding({ parentPath, kind }); setAddName(''); };
  const submitAdd = async () => {
    const name = addName.trim();
    if (!name || !adding) { setAdding(null); return; }
    const path = adding.parentPath ? `${adding.parentPath}/${name}` : name;
    const kind = adding.kind;
    setAdding(null); setAddName('');
    try {
      const res = await v2ConnectorsService.files.create(connectorId, { path, is_directory: kind === 'dir', content: '' }, marketplaceId);
      await load(kind === 'dir' ? undefined : res.id);
    } catch (err) {
      setError(err.message || 'Failed to create (a file or folder with that path may already exist)');
    }
  };

  const saveFile = async () => {
    if (!selectedFile) return;
    try {
      await v2ConnectorsService.files.update(connectorId, selectedFile.id, { content: fileContent }, marketplaceId);
      setFileDirty(false);
      setFiles(prev => prev.map(f => f.id === selectedFile.id ? { ...f, content: fileContent } : f));
    } catch (err) {
      setError(err.message || 'Failed to save file');
    }
  };

  const doDelete = async () => {
    const cd = confirmDelete;
    setConfirmDelete(null);
    try {
      if (cd.all) {
        await v2ConnectorsService.files.removeAll(connectorId, marketplaceId);
        setSelectedKey(null); setFileContent('');
      } else {
        await v2ConnectorsService.files.remove(connectorId, cd.fileId, cd.dirPath, marketplaceId);
        if (cd.fileId && cd.fileId === selectedKey) { setSelectedKey(null); setFileContent(''); }
      }
      await load();
    } catch (err) {
      setError(err.message || 'Failed to delete');
    }
  };

  const moveFile = async (fileId, targetDir) => {
    const f = files.find(x => x.id === fileId);
    if (!f || f.is_directory) return;
    const filename = f.path.split('/').pop();
    const newPath = targetDir ? `${targetDir}/${filename}` : filename;
    if (newPath === f.path) return;
    try {
      await v2ConnectorsService.files.update(connectorId, fileId, { path: newPath }, marketplaceId);
      await load(fileId);
    } catch (err) {
      setError(err.message || 'Failed to move file (a file with that name may already exist there)');
    }
  };
  const onFileDragStart = (e, fileId) => { e.dataTransfer.setData('text/plain', String(fileId)); e.dataTransfer.effectAllowed = 'move'; };
  const onFolderDrop = (e, dirPath) => { e.preventDefault(); e.stopPropagation(); setDragOverPath(null); const id = Number(e.dataTransfer.getData('text/plain')); if (id) moveFile(id, dirPath); };

  const entryRequire = entrypoint ? './' + String(entrypoint).replace(/\.js$/i, '') : './index';
  const zipBaseName = ((assistantContext && assistantContext.name) || 'connector').replace(/[^a-z0-9-_]+/gi, '_') || 'connector';

  const downloadZip = async () => {
    try {
      setBusy('zip'); setError(null);
      const zip = new JSZip();
      // The user's own files go in verbatim first...
      const provided = new Set(files.filter(f => !f.is_directory).map(f => f.path));
      files.forEach(f => { if (f.is_directory) zip.folder(f.path); else zip.file(f.path, f.content || ''); });
      // ...then platform defaults ONLY where the code doesn't already provide the file. This keeps a
      // hand-authored package.json (deps, engines, main) intact instead of overwriting it with a stub.
      zip.file('connector-wrapper.js', CONNECTOR_WRAPPER_SOURCE); // reserved: always the platform base class
      if (!provided.has('handler.js')) zip.file('handler.js', buildHandlerSource(entryRequire));
      if (!provided.has('package.json')) {
        zip.file('package.json', JSON.stringify({
          name: zipBaseName, version: '1.0.0', private: true,
          description: 'HCONNECT connector bundle', main: entrypoint || 'index.js', handler: 'handler.handler',
        }, null, 2) + '\n');
      }
      if (!provided.has('README.md')) {
        zip.file('README.md', [
          `# ${(assistantContext && assistantContext.name) || 'Connector'} bundle`, '',
          'Runnable connector bundle. The code is shared across the marketplace; this connector\'s entry point is:',
          '', `- **entry point:** \`${entrypoint || '(none set)'}\``, '',
          '- `handler.js` — Lambda-style `exports.handler`; requires the entry point above.',
          '- `connector-wrapper.js` — the shared base class (platform-provided; do not edit).',
          '- other files — the marketplace\'s shared connector code.', '',
          'Run:', '', '```js',
          "const { handler } = require('./handler');",
          'await handler({ credentialId }, { mysql, pwkey });',
          '```', '',
        ].join('\n'));
      }
      const blob = await zip.generateAsync({ type: 'blob' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = `${zipBaseName}.zip`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      setError('Failed to build zip: ' + (err.message || err));
    } finally { setBusy(null); }
  };

  const uploadZip = async (file) => {
    if (!file) return;
    if (!canEdit) { setError('Set a Marketplace on this connector before uploading shared code.'); return; }
    setBusy('zip'); setError(null);
    try {
      const zip = await JSZip.loadAsync(file);
      const entries = [];
      zip.forEach((rel, entry) => { if (!entry.dir) entries.push(entry); });
      const existingByPath = new Map(files.map(f => [f.path, f]));
      let lastId = null;
      for (const entry of entries) {
        const path = entry.name.replace(/^\/+/, '');
        if (path.startsWith('__MACOSX/') || path.endsWith('.DS_Store')) continue;
        if (path === 'connector-wrapper.js') continue; // reserved: the platform base class
        const content = await entry.async('string');
        const existing = existingByPath.get(path);
        if (existing) await v2ConnectorsService.files.update(connectorId, existing.id, { content }, marketplaceId);
        else { const r = await v2ConnectorsService.files.create(connectorId, { path, is_directory: false, content }, marketplaceId); lastId = r.id; }
      }
      await load(lastId);
    } catch (err) {
      setError('Failed to read zip: ' + (err.message || err));
    } finally { setBusy(null); }
  };

  const zipBtn = { display: 'inline-flex', alignItems: 'center', gap: 5, padding: '0.35rem 0.7rem', fontSize: '0.78rem', fontWeight: 600, color: '#374151', background: '#fff', border: '1px solid #d1d5db', borderRadius: 6, cursor: 'pointer' };
  const miniBtn = { border: 'none', background: 'transparent', cursor: 'pointer', fontSize: '0.72rem', color: '#4a6cf7', padding: '0 2px' };

  const addBtns = (parentPath) => canAdd ? (
    <>
      <button type="button" title="New file here" onClick={(e) => { e.stopPropagation(); startAdd(parentPath, 'file'); }} style={miniBtn}>＋file</button>
      <button type="button" title="New folder here" onClick={(e) => { e.stopPropagation(); startAdd(parentPath, 'dir'); }} style={{ ...miniBtn, color: '#6b7280' }}>＋dir</button>
    </>
  ) : null;

  const renderAddInput = (parentPath, depth) => (adding && adding.parentPath === parentPath) ? (
    <div style={{ display: 'flex', gap: 4, alignItems: 'center', padding: '0.3rem 0.5rem', paddingLeft: 8 + depth * 14 }}>
      <span style={{ fontSize: '0.8rem' }}>{adding.kind === 'dir' ? '📁' : '📄'}</span>
      <input autoFocus value={addName} onChange={e => setAddName(e.target.value)}
        onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); submitAdd(); } if (e.key === 'Escape') setAdding(null); }}
        placeholder={adding.kind === 'dir' ? 'folder name' : 'file name'}
        style={{ flex: 1, minWidth: 0, border: '1px solid #4a6cf7', borderRadius: 6, padding: '0.25rem 0.4rem', fontSize: '0.75rem' }} />
      <button type="button" onClick={submitAdd} style={{ ...miniBtn, color: '#16a34a', fontSize: '0.85rem' }}>✓</button>
      <button type="button" onClick={() => setAdding(null)} style={{ ...miniBtn, color: '#9ca3af', fontSize: '0.85rem' }}>✕</button>
    </div>
  ) : null;

  const tree = buildTree(files);

  const renderNode = (node, depth) => {
    const kids = Object.values(node.children).sort((a, b) => (b.isDir - a.isDir) || a.name.localeCompare(b.name));
    return kids.map(child => {
      return (
        <React.Fragment key={child.path}>
          <div
            onClick={() => selectFileNode(child)}
            draggable={!child.isDir}
            onDragStart={!child.isDir ? (e) => onFileDragStart(e, child.id) : undefined}
            onDragOver={child.isDir ? (e) => { e.preventDefault(); e.stopPropagation(); e.dataTransfer.dropEffect = 'move'; setDragOverPath(child.path); } : undefined}
            onDragLeave={child.isDir ? () => setDragOverPath(p => (p === child.path ? null : p)) : undefined}
            onDrop={child.isDir ? (e) => onFolderDrop(e, child.path) : undefined}
            title={child.isDir ? 'Drop a file here to move it into this folder' : 'Drag to a folder to move'}
            style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              padding: '0.32rem 0.5rem', paddingLeft: 8 + depth * 14, cursor: child.isDir ? 'default' : 'grab',
              background: (child.isDir && dragOverPath === child.path) ? '#dcfce7' : (!child.isDir && child.id === selectedKey) ? '#eef2ff' : 'transparent',
              outline: (child.isDir && dragOverPath === child.path) ? '1px dashed #16a34a' : 'none',
              color: (!child.isDir && child.id === selectedKey) ? '#4338ca' : '#374151',
              fontSize: '0.8rem', fontWeight: child.isDir ? 600 : ((!child.isDir && child.id === selectedKey) ? 600 : 400),
            }}
          >
            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {child.isDir ? '📁 ' : '📄 '}{child.name}
            </span>
            <span style={{ display: 'flex', alignItems: 'center', gap: 4, marginLeft: 6 }}>
              {child.isDir && addBtns(child.path)}
              <span onClick={(e) => { e.stopPropagation(); setConfirmDelete(child.isDir ? { dirPath: child.path, label: child.path + '/' } : { fileId: child.id, label: child.path }); }}
                title="Delete" style={{ color: '#dc2626' }}>🗑️</span>
            </span>
          </div>
          {child.isDir && renderAddInput(child.path, depth + 1)}
          {child.isDir && renderNode(child, depth + 1)}
        </React.Fragment>
      );
    });
  };

  const availableFiles = files.filter(f => !f.is_directory).map(f => f.path);

  return (
    <div>
      {error && <div className="error" style={{ marginBottom: 8 }}>{error}</div>}
      {connectorId && !marketplaceId && (
        <div style={{ background: '#fffbeb', border: '1px solid #fde68a', color: '#92400e', borderRadius: 8, padding: '0.5rem 0.75rem', fontSize: '0.8rem', marginBottom: 8 }}>
          Set a Marketplace on this connector to work with its shared code.
        </div>
      )}

      <div style={{ display: 'flex', gap: 8, marginBottom: 10, alignItems: 'center' }}>
        {canEdit && (
          <button type="button" onClick={initialize} disabled={!!busy} title="Create a starter skeleton (entry point + test + package.json)"
            style={{ ...zipBtn, color: '#fff', background: '#4a6cf7', borderColor: '#4a6cf7' }}>✨ Initialize</button>
        )}
        <button type="button" onClick={downloadZip} disabled={busy === 'zip'} style={zipBtn}>⬇ Download zip</button>
        <button type="button" onClick={() => uploadRef.current && uploadRef.current.click()} disabled={busy === 'zip' || !canEdit} style={{ ...zipBtn, opacity: canEdit ? 1 : 0.6 }}>⬆ Upload zip</button>
        <input ref={uploadRef} type="file" accept=".zip,application/zip" style={{ display: 'none' }}
          onChange={(e) => { const f = e.target.files[0]; e.target.value = ''; uploadZip(f); }} />
        {canEdit && files.length > 0 && (
          <button type="button" onClick={() => setConfirmDelete({ all: true })} disabled={busy === 'zip'} style={{ ...zipBtn, color: '#b91c1c', borderColor: '#fecaca' }}>🗑 Delete all</button>
        )}
        {busy && <span style={{ fontSize: '0.78rem', color: '#6b7280' }}>working…</span>}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '240px 1fr', gap: 12, alignItems: 'start' }}>
        {/* Explorer */}
        <div style={{ border: '1px solid #e5e7eb', borderRadius: 8, overflow: 'hidden' }}>
          <div
            onDragOver={(e) => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; setDragOverPath('__root__'); }}
            onDragLeave={() => setDragOverPath(p => (p === '__root__' ? null : p))}
            onDrop={(e) => onFolderDrop(e, '')}
            style={{ maxHeight: 380, overflowY: 'auto', outline: dragOverPath === '__root__' ? '1px dashed #16a34a' : 'none', background: dragOverPath === '__root__' ? '#f0fdf4' : 'transparent' }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.28rem 0.5rem', fontSize: '0.68rem', color: '#9ca3af' }}>
              <span>{(assistantContext && assistantContext.marketplaceName) || 'marketplace'} code (shared)</span>
              <span style={{ display: 'flex', gap: 4 }}>{addBtns('')}</span>
            </div>
            {renderAddInput('', 0)}
            {renderNode(tree, 0)}
            {files.length === 0 && !adding && (
              <div style={{ padding: '0.4rem 0.5rem', fontSize: '0.72rem', color: '#9ca3af' }}>
                {canAdd ? 'No files yet. Use ＋file / ＋dir above, then ★ to pick the entry point.' : 'Set a Marketplace to add shared code.'}
              </div>
            )}
          </div>
        </div>

        {/* Editor */}
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6, gap: 8 }}>
            <span style={{ fontSize: '0.8rem', fontWeight: 600, color: '#111827', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {selectedFile ? selectedName : 'Select a file'}
            </span>
            {selectedFile && (
              <span style={{ display: 'flex', gap: 8, alignItems: 'center', flexShrink: 0 }}>
                <button type="button" onClick={insertSkeleton} title="Insert a ConnectorWrapper skeleton into this file"
                  style={{ padding: '0.3rem 0.7rem', fontSize: '0.78rem', fontWeight: 600, color: '#4a6cf7', background: '#fff', border: '1px solid #d1d5db', borderRadius: 6, cursor: 'pointer' }}>
                  Skeleton Code
                </button>
                <button type="button" onClick={saveFile} disabled={!fileDirty}
                  style={{ padding: '0.3rem 0.7rem', fontSize: '0.78rem', fontWeight: 600, color: '#fff', background: fileDirty ? '#16a34a' : '#9ca3af', border: 'none', borderRadius: 6, cursor: fileDirty ? 'pointer' : 'default' }}>
                  {fileDirty ? 'Save file' : 'Saved'}
                </button>
              </span>
            )}
          </div>
          <textarea
            value={selectedFile ? fileContent : ''}
            onChange={e => onEditorChange(e.target.value)}
            disabled={!selectedFile}
            spellCheck={false}
            placeholder={selectedFile ? '' : 'Select a file from the tree, or add one with ＋file.'}
            style={{ width: '100%', boxSizing: 'border-box', minHeight: 320, border: '1px solid #d1d5db', borderRadius: 8, padding: '0.6rem', fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', fontSize: '0.78rem', whiteSpace: 'pre', overflowWrap: 'normal', overflowX: 'auto', background: selectedFile ? '#fff' : '#f9fafb' }}
          />
        </div>
      </div>

      <div style={{ marginTop: 12 }}>
        <ConnectorCodeAssistant
          context={{ ...assistantContext, currentCode: selectedFile ? fileContent : '', currentFile: selectedName, availableFiles, entrypoint: entrypoint || null }}
          onInsert={onAssistantInsert}
        />
      </div>

      {confirmDelete && (
        <ConfirmModal
          title={confirmDelete.all ? 'Delete all files' : confirmDelete.dirPath ? 'Delete folder' : 'Delete file'}
          message={confirmDelete.all
            ? 'Delete ALL shared code for this marketplace? This cannot be undone and affects every connector of this marketplace.'
            : confirmDelete.dirPath
              ? `Delete "${confirmDelete.label}" and everything inside it? This is shared across the marketplace.`
              : `Delete "${confirmDelete.label}"? This is shared across the marketplace.`}
          confirmLabel="Delete"
          danger
          onConfirm={doDelete}
          onCancel={() => setConfirmDelete(null)}
        />
      )}
    </div>
  );
};

export default ConnectorFiles;
